import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'straw.teaser.seenAt';
const REPLAY_AFTER_MS = 60 * 60 * 1000; // 1 hour — re-show on return visits

interface Props {
  src?: string;
}

/**
 * Full-screen cinematic teaser played on first menu open in a session.
 *
 * - Auto-plays muted (browsers require muted for autoplay).
 * - Skip button appears immediately; the video also auto-dismisses on `ended`.
 * - Skips entirely if the user dismissed it less than REPLAY_AFTER_MS ago.
 */
export function TeaserIntro({ src = '/videos/teaser.mp4' }: Props): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const last = Number(window.sessionStorage.getItem(STORAGE_KEY) ?? 0);
    if (Date.now() - last > REPLAY_AFTER_MS) {
      setOpen(true);
    }
  }, []);

  function dismiss(): void {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(STORAGE_KEY, String(Date.now()));
    }
    setOpen(false);
  }

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          key="teaser-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-onyx-900"
        >
          <video
            ref={videoRef}
            src={src}
            autoPlay
            muted
            playsInline
            onEnded={dismiss}
            className="h-full w-full object-cover"
          />
          <button
            onClick={dismiss}
            className="absolute top-6 left-6 rounded-full border border-aurum-500/40 bg-onyx-900/60 px-4 py-2 font-mono text-xs uppercase tracking-widest text-cream-50 backdrop-blur hover:bg-aurum-500/20"
          >
            تخطّي ›
          </button>
          <div className="pointer-events-none absolute bottom-12 left-0 right-0 text-center">
            <p className="aurum-gradient-text font-display text-5xl">Straw</p>
            <p className="mt-2 font-body text-xs uppercase tracking-[0.4em] text-cream-100/70">
              تجربة قهوة فاخرة مصنوعة لكم بحب
            </p>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
